import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { api, ApiClientError } from '../lib/api';
import { downloadDocument } from '../lib/downloadDocument';
import { Badge, Button, Checkbox, Select } from '../atoms';
import { PageHeader } from '../shell';
import type { BadgeVariant } from '../atoms';
import { DataTable, Modal, useToast } from '../organisms';
import { formatDate } from '../lib/orgTimezone';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * DocumentReviewQueue — /v2/documents/review-queue
 *
 * Staff triage screen for client-submitted documents (Domain 09.2). Lists
 * documents whose review_status is client_submitted / in_review (org-scoped
 * on the backend) and lets staff act on each:
 *   - Accept  → review_status = 'accepted'   (clears it from the queue)
 *   - Reject  → review_status = 'rejected'   (+ note; backend notifies client)
 *   - Route   → review_status = 'in_review'  (keep for follow-up)
 *
 * Rows are CLICKABLE → the document detail (/documents/:id). A document can be
 * ASSIGNED to a staff reviewer (assignee dropdown per row), and the queue can
 * filter to "assigned to me".
 *
 * Wires the REAL backend:
 *   GET   /api/documents/review-queue[?status=&assignee=me]  (list)
 *   PATCH /api/documents/:id/review                          (decision)
 *   PATCH /api/documents/:id/assign                          (assignee)
 *   GET   /api/users-list                                    (assignee options)
 * On success the list query is invalidated so the queue refreshes.
 */

type ReviewStatus = 'client_submitted' | 'in_review' | 'accepted' | 'rejected';

interface ReviewDoc {
  id: string;
  name: string;
  document_type: string | null;
  filename: string;
  file_size_bytes: number | null;
  cadence: string | null;
  review_status: ReviewStatus;
  review_note: string | null;
  submitted_at: string;
  client_id: string | null;
  client_name: string | null;
  uploaded_by_user_id: string | null;
  uploaded_by_email: string | null;
  assigned_to_user_id: string | null;
  assigned_to_email: string | null;
  assigned_at: string | null;
}

interface StaffUser {
  id: string;
  email: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
}

type Decision = 'accepted' | 'rejected' | 'in_review';

interface ReviewPayload {
  id: string;
  review_status: Decision;
  review_note?: string;
}

interface AssignPayload {
  id: string;
  assigned_to_user_id: string | null;
}

/** Portal/client tiers can never be a staff reviewer — kept out of the picker. */
const NON_STAFF_ROLES = new Set(['client', 'consultant']);

function staffLabel(u: StaffUser): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name || u.email;
}

const STATUS_FILTERS = [
  { value: 'queue', label: 'All in queue' },
  { value: 'client_submitted', label: 'Client submitted' },
  { value: 'in_review', label: 'In review' },
] as const;

const STATUS_BADGE: Record<ReviewStatus, { variant: BadgeVariant; label: string }> = {
  client_submitted: { variant: 'info', label: 'Client submitted' },
  in_review: { variant: 'warning', label: 'In review' },
  accepted: { variant: 'success', label: 'Accepted' },
  rejected: { variant: 'danger', label: 'Rejected' },
};

export function DocumentReviewQueue() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<string>('queue');
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [rejectDoc, setRejectDoc] = useState<ReviewDoc | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  // Assign / Route / Reject / Accept all PATCH the document (assignee or review_status)
  // → gate on document.can_edit (staff/agent hold it; hides the controls from
  // view-only training/external roles). Download stays open to any viewer.
  const canEdit = useEntityPermission('document', 'can_edit');

  const params = new URLSearchParams();
  if (filter !== 'queue') params.set('status', filter);
  if (assignedToMe) params.set('assignee', 'me');
  const qs = params.toString();
  const listPath = qs ? `/api/documents/review-queue?${qs}` : '/api/documents/review-queue';

  const query = useQuery<ReviewDoc[]>({
    queryKey: ['documents-review-queue', filter, assignedToMe],
    queryFn: ({ signal }) => api.get<ReviewDoc[]>(listPath, signal),
  });

  // Staff users for the assignee picker (portal/client tiers excluded).
  const staffQuery = useQuery<StaffUser[]>({
    queryKey: ['users-list'],
    queryFn: ({ signal }) => api.get<StaffUser[]>('/api/users-list', signal),
    staleTime: 5 * 60 * 1000,
  });
  const staff = useMemo(
    () => (staffQuery.data ?? []).filter((u) => !NON_STAFF_ROLES.has(u.role)),
    [staffQuery.data],
  );

  const assignMutation = useMutation<unknown, Error, AssignPayload>({
    mutationFn: ({ id, assigned_to_user_id }) =>
      api.patch(`/api/documents/${id}/assign`, { assigned_to_user_id }),
    onSuccess: (_data, vars) => {
      toast({ variant: 'success', message: vars.assigned_to_user_id ? 'Document assigned.' : 'Document unassigned.' });
      queryClient.invalidateQueries({ queryKey: ['documents-review-queue'] });
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  const mutation = useMutation<unknown, Error, ReviewPayload>({
    mutationFn: ({ id, review_status, review_note }) =>
      api.patch(`/api/documents/${id}/review`, { review_status, review_note }),
    onSuccess: (_data, vars) => {
      const verb = vars.review_status === 'accepted' ? 'accepted'
        : vars.review_status === 'rejected' ? 'rejected'
        : 'moved to review';
      toast({ variant: 'success', message: `Document ${verb}.` });
      queryClient.invalidateQueries({ queryKey: ['documents-review-queue'] });
      queryClient.invalidateQueries({ queryKey: ['documents-list'] });
      setRejectDoc(null);
      setRejectNote('');
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  const columns = useMemo<ColumnDef<ReviewDoc>[]>(() => [
    { accessorKey: 'name', header: 'Document' },
    {
      accessorKey: 'document_type',
      header: 'Type',
      cell: ({ getValue }) => {
        const v = getValue() as string | null;
        return v ? <Badge variant="neutral">{v}</Badge> : '—';
      },
    },
    {
      accessorKey: 'client_name',
      header: 'Client',
      cell: ({ getValue }) => (getValue() as string | null) || '—',
    },
    {
      accessorKey: 'uploaded_by_email',
      header: 'Submitted by',
      cell: ({ getValue }) => {
        const v = getValue() as string | null;
        return v ? (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{v}</span>
        ) : '—';
      },
    },
    {
      accessorKey: 'submitted_at',
      header: 'Submitted',
      cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }),
    },
    {
      accessorKey: 'review_status',
      header: 'Status',
      cell: ({ getValue }) => {
        const s = getValue() as ReviewStatus;
        const b = STATUS_BADGE[s] ?? { variant: 'neutral' as BadgeVariant, label: s };
        return <Badge variant={b.variant}>{b.label}</Badge>;
      },
    },
    {
      id: 'assignee',
      accessorKey: 'assigned_to_user_id',
      header: 'Assigned to',
      // The assignee picker is interactive — stop the row-click from firing so
      // changing the assignee never navigates to the document. View-only roles
      // (no document.can_edit) see the current assignee read-only, not the picker.
      cell: ({ row }) => {
        const doc = row.original;
        if (!canEdit) {
          return doc.assigned_to_email
            ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{doc.assigned_to_email}</span>
            : <span style={{ color: 'var(--color-ink-3)' }}>—</span>;
        }
        return (
          <div onClick={(e) => e.stopPropagation()} style={{ minWidth: 160 }}>
            <Select
              aria-label={`Assign ${doc.name}`}
              value={doc.assigned_to_user_id ?? ''}
              disabled={assignMutation.isPending}
              onChange={(e) => assignMutation.mutate({ id: doc.id, assigned_to_user_id: e.target.value || null })}
              options={[
                { value: '', label: 'Unassigned' },
                ...staff.map((u) => ({ value: u.id, label: staffLabel(u) })),
              ]}
            />
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const doc = row.original;
        const busy = mutation.isPending;
        return (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => downloadDocument(doc.id, (m) => toast({ variant: 'error', message: m }))}
            >
              ↓
            </Button>
            {canEdit && doc.review_status !== 'in_review' && (
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => mutation.mutate({ id: doc.id, review_status: 'in_review' })}
              >
                Route
              </Button>
            )}
            {canEdit && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => { setRejectDoc(doc); setRejectNote(''); }}
                >
                  Reject
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy}
                  onClick={() => mutation.mutate({ id: doc.id, review_status: 'accepted' })}
                >
                  Accept
                </Button>
              </>
            )}
          </div>
        );
      },
    },
  ], [mutation, assignMutation, staff, canEdit]);

  const docs = query.data ?? [];

  return (
    <>
      <PageHeader
        title="Document Review Queue"
        subtitle={query.isLoading ? 'Loading…' : `${docs.length} document${docs.length === 1 ? '' : 's'} awaiting review`}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
            {/* 7/28 C4: start an information request straight from the review queue. */}
            <Button variant="secondary" onClick={() => navigate('/information-requests')}>New request</Button>
            <Checkbox
              label="Assigned to me"
              checked={assignedToMe}
              onChange={(e) => setAssignedToMe(e.target.checked)}
            />
            <div style={{ width: 220, maxWidth: '100%' }}>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                options={STATUS_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
              />
            </div>
          </div>
        }
      />

      {query.error && (
        <div style={{
          padding: 'var(--sp-4)',
          background: 'var(--color-status-red-bg)',
          border: '1px solid var(--color-status-red)',
          color: 'var(--color-status-red)',
          borderRadius: 'var(--r-lg)',
          marginBottom: 'var(--sp-4)',
        }}>
          <strong>Failed to load review queue:</strong> {(query.error as Error).message}
        </div>
      )}

      {!query.isLoading && docs.length === 0 && !query.error && (
        <div style={{
          padding: 'var(--sp-8)',
          background: 'var(--color-card)',
          border: '1px dashed var(--color-border)',
          borderRadius: 'var(--r-lg)',
          color: 'var(--color-muted)',
          textAlign: 'center',
        }}>
          <h2 style={{
            margin: '0 0 var(--sp-2)',
            fontSize: 'var(--fs-xl)',
            fontWeight: 'var(--fw-semibold)',
            color: 'var(--color-ink-3)',
          }}>Queue is clear</h2>
          <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>
            No client-submitted documents are waiting for review.
          </p>
        </div>
      )}

      {docs.length > 0 && (
        <DataTable
          data={docs}
          columns={columns}
          onRowClick={(doc) => navigate(`/documents/${doc.id}`)}
        />
      )}

      <Modal
        open={!!rejectDoc}
        onClose={() => { setRejectDoc(null); setRejectNote(''); }}
        title="Reject document"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
            <Button variant="ghost" size="sm" onClick={() => { setRejectDoc(null); setRejectNote(''); }}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={mutation.isPending}
              onClick={() => {
                if (!rejectDoc) return;
                mutation.mutate({ id: rejectDoc.id, review_status: 'rejected', review_note: rejectNote.trim() || undefined });
              }}
            >
              Reject & notify client
            </Button>
          </div>
        }
      >
        <p style={{ margin: '0 0 var(--sp-3)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
          Rejecting <strong style={{ color: 'var(--color-ink)' }}>{rejectDoc?.name}</strong>
          {rejectDoc?.client_name ? ` from ${rejectDoc.client_name}` : ''}. The client is notified with your reason.
        </p>
        <label style={{
          display: 'block',
          marginBottom: 'var(--sp-1)',
          fontSize: 'var(--fs-sm)',
          fontWeight: 'var(--fw-medium)',
          color: 'var(--color-ink-2)',
        }}>
          Reason (optional)
        </label>
        <textarea
          value={rejectNote}
          onChange={(e) => setRejectNote(e.target.value)}
          rows={3}
          placeholder="e.g. Wrong tax year — please re-upload the 2026 statement."
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: 'var(--fs-md)',
            fontFamily: 'inherit',
            color: 'var(--color-ink)',
            background: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-md)',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
        />
      </Modal>
    </>
  );
}
